import useFieldId from 'core/hooks/useFieldId';
import { useTranslate } from 'core/hooks/useTranslate';
import { Ref, useEffect } from 'react';
import { VALIDATION_CONFIG, ValidationType } from '../ValidationMessage/validationConfig';

type UseInputTextParams = {
  disabled?: boolean;
  error?: string | React.ReactNode;
  helperText?: string;
  icon?: string;
  iconAriaLabel?: string;
  name?: string;
  onClickIcon?: (...args: unknown[]) => void;
  ref?: Ref<HTMLInputElement>;
  referer?: React.RefObject<HTMLInputElement>;
  success?: string | React.ReactNode;
};

/**
 * Returns true if the value is valid renderable content (string or ReactNode),
 * avoiding pitfalls of JS truthiness.
 */
const hasContent = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'boolean') return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true; // valid ReactNode
};

type AriaDescribedByParams = {
  fieldId: ReturnType<typeof useFieldId>;
  hasHelperText: boolean;
  hasValidation: boolean;
};

/**
 * Builds the aria-describedby attribute by concatenating the IDs of the
 * helper text and validation message elements when they are present.
 */
function getAriaDescribedBy({ fieldId, hasHelperText, hasValidation }: AriaDescribedByParams): string | undefined {
  const ids: string[] = [];
  if (hasHelperText) ids.push(fieldId.helperId);
  if (hasValidation) ids.push(fieldId.validationId);
  return ids.length > 0 ? ids.join(' ') : undefined;
}

type ValidationState = {
  type: ValidationType | null;
  message: string | React.ReactNode | null;
};

function getValidation(error: unknown, success: unknown, hasError: boolean, hasSuccess: boolean): ValidationState {
  if (hasError) return { type: 'error', message: error as string | React.ReactNode };
  if (hasSuccess) return { type: 'success', message: success as string | React.ReactNode };
  return { type: null, message: null };
}

function useInputText({ disabled, error, helperText, icon, iconAriaLabel, name, onClickIcon, ref, referer, success }: UseInputTextParams) {
  const { t } = useTranslate();

  const refInput = referer ?? ref;

  const fieldId = useFieldId(name ? `input-text-${name}` : 'input-text');

  const getIconAriaLabel = (iconType: string): string => {
    const iconLabels: Record<string, string> = {
      visible: t('buttons:Show password'),
      'visible-off': t('buttons:Hide password'),
      search: t('buttons:Search'),
      clear: t('buttons:Clear field'),
      calendar: t('buttons:Open calendar'),
      eye: t('buttons:Show password'),
      'eye-off': t('buttons:Hide password'),
    };

    return iconLabels[iconType] || t('buttons:Click {{icon}} icon', { icon: iconType });
  };

  useEffect(() => {
    let isMouseDown = false;
    let keyboardNavTimeout: ReturnType<typeof setTimeout> | null = null;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Tab') {
        document.body.classList.add('keyboard-nav');
        if (keyboardNavTimeout) {
          clearTimeout(keyboardNavTimeout);
        }
        keyboardNavTimeout = setTimeout(() => {
          document.body.classList.remove('keyboard-nav');
        }, 150);
      }
    }

    function handleMouseDown() {
      isMouseDown = true;
      document.body.classList.remove('keyboard-nav');
      if (keyboardNavTimeout) {
        clearTimeout(keyboardNavTimeout);
        keyboardNavTimeout = null;
      }
      setTimeout(() => {
        isMouseDown = false;
      }, 0);
    }

    function handleFocusIn(e: FocusEvent) {
      const target = e.target as HTMLElement;
      if (target.classList.contains('input-text__input')) {
        if (document.body.classList.contains('keyboard-nav') && !isMouseDown) {
          target.classList.add('focused-by-keyboard');
          target.classList.remove('focused-by-mouse');
        } else {
          target.classList.add('focused-by-mouse');
          target.classList.remove('focused-by-keyboard');
        }
      }
    }

    function handleFocusOut(e: FocusEvent) {
      const target = e.target as HTMLElement;
      if (target.classList.contains('input-text__input')) {
        target.classList.remove('focused-by-keyboard', 'focused-by-mouse');
      }
    }

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('focusout', handleFocusOut, true);

    return () => {
      if (keyboardNavTimeout) {
        clearTimeout(keyboardNavTimeout);
      }
      document.body.classList.remove('keyboard-nav');
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('focusout', handleFocusOut, true);
    };
  }, []);


  const getIconAriaLabelValue = icon ? (iconAriaLabel || getIconAriaLabel(icon)) : undefined;

  const hasError = hasContent(error);
  const hasSuccess = hasContent(success);

  const { type: validationType, message: validationMessage } = getValidation(error, success, hasError, hasSuccess);

  const shouldRenderValidation = hasError || hasSuccess;
  const ariaDescribedBy = getAriaDescribedBy({
    fieldId,
    hasHelperText: !!helperText,
    hasValidation: shouldRenderValidation,
  });

  const isIconClickable = typeof onClickIcon === 'function' && !disabled;

  return {
    refInput,
    getIconAriaLabelValue,
    fieldId,
    hasError,
    hasSuccess,
    isIconClickable,
    shouldRenderValidation,
    ariaDescribedBy,
    validationType,
    validationMessage,
    validationConfig: VALIDATION_CONFIG,
  };
}

export default useInputText;
